import React, { FC } from 'react'
import {
  NotificationData,
  NotificationProperty,
  NotificationTypeAttack,
  NotificationTypeLibrary,
  NotificationTypeVulnerability
} from './shared'
import {
  IconComment,
  IconWarning,
  IconConfirmation,
  IconNegative,
  IconInformation,
  IconServer,
  IconApplication,
  IconDownload,
  IconProps,
  IconVulnerability
} from '@contrast-security-inc/design-system-foundations'

interface NotificationIconProps {
  sourceType: NotificationData['sourceType']
  type: NotificationData['type']
  properties: Array<{
    value: NotificationProperty['value']
  }>
}

type SkeletorIcon = React.ForwardRefExoticComponent<
  IconProps & React.RefAttributes<SVGSVGElement>
>

export const iconConfigs: {
  [key: string]: Partial<IconProps> & {
    testId: string
    Icon: SkeletorIcon
  }
} = {
  comment: { Icon: IconComment, testId: 'notification-icon-comment' },
  application: {
    Icon: IconApplication,
    testId: 'notification-icon-application'
  },
  warning: { Icon: IconWarning, testId: 'notification-icon-warning' },
  confirmation: {
    Icon: IconConfirmation,
    testId: 'notification-icon-confirmation'
  },
  negative: {
    Icon: IconNegative,
    testId: 'notification-icon-negative'
  },
  information: {
    Icon: IconInformation,
    testId: 'notification-icon-information'
  },
  download: {
    Icon: IconDownload,
    testId: 'notification-icon-download'
  },
  server: { Icon: IconServer, testId: 'notification-icon-server' },
  vulnerability: {
    Icon: IconVulnerability,
    testId: 'notification-icon-vulnerability'
  }
}

const defaultConfig = iconConfigs.information

const getIconAndAdditionalProps: (
  props: NotificationIconProps
) => Partial<IconProps> & { Icon: SkeletorIcon } = ({
  sourceType,
  type,
  properties
}) => {
  switch (sourceType) {
    case 'ATTACK':
      switch (type as NotificationTypeAttack) {
        case 'ATTACK_EVENT_COMMENT':
          return iconConfigs.comment
        case 'NEW_ATTACK':
        case 'ATTACK_UPDATE':
        case 'ATTACK_END':
        case 'NEW_ATTACK_APPLICATION':
          return iconConfigs.application
        default:
          return defaultConfig
      }
    case 'VULNERABILITY':
      switch (type as NotificationTypeVulnerability) {
        case 'NEW_VULNERABILITY_COMMENT':
          return iconConfigs.comment
        case 'NEW_VULNERABILITY':
        case 'VULNERABILITY_CHANGESTATUS_OPEN':
          return iconConfigs.vulnerability
        case 'VULNERABILITY_CHANGESTATUS_CLOSED':
          return iconConfigs.confirmation
        case 'APPROVAL_WORKFLOW':
          if (
            properties.length &&
            properties[0].value ===
              'detail_notificationApprovalWorkflow_resolution_approved'
          ) {
            return { ...iconConfigs.confirmation, color: 'green50' }
          } else if (
            properties.length &&
            properties[0].value ===
              'detail_notificationApprovalWorkflow_resolution_denied'
          ) {
            return iconConfigs.negative
          }
          return defaultConfig
        case 'APPROVAL_WORKFLOW_STATUS_UPDATED':
          return iconConfigs.information
        default:
          return defaultConfig
      }
    case 'LIBRARY':
      switch (type as NotificationTypeLibrary) {
        case 'NEW_VULNERABLE_LIBRARY':
          return iconConfigs.vulnerability
        case 'LIBRARY_VIOLATION':
          return iconConfigs.information
        default:
          return defaultConfig
      }
    case 'SERVER':
      return iconConfigs.server
    case 'APPLICATION':
    case 'ONBOARDING_FAIL':
      return iconConfigs.application
    case 'REPORT_FAIL':
      return iconConfigs.vulnerability
    case 'REPORT_SUCCESS':
      return iconConfigs.download
    case 'APPLICENSE':
    case 'SECURITYCONTROL':
    case 'REMEDIATION_POLICY':
    case 'COMPLIANCE_POLICY':
    case 'BUGTRACKER':
    case 'AGENT_UPDATE':
    case 'WEBHOOK':
    default:
      return defaultConfig
  }
}

export const NotificationIcon: FC<NotificationIconProps> = ({
  sourceType,
  type,
  properties
}) => {
  const { Icon, ...props } = getIconAndAdditionalProps({
    sourceType,
    type,
    properties
  })
  return <Icon size="md" color="neutral70" {...props} />
}
